<!-- RemoveFromWatchlistModal.vue -->
<script setup lang="ts">
import { computed } from 'vue'
import Badge from 'primevue/badge'
import type { Tender } from '@/types/tender'
import { getBadgeStatus, getWorkflowLabel } from '@/utils/badgeUtils'

const props = defineProps<{
  tender: Tender | null
  loading: boolean
}>()

defineEmits<{
  confirm: []
  close: []
}>()

// Format date
const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A'
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return dateString
  }
}

// Get status label
const getStatusLabel = (status: Tender['status']): string => {
  return getWorkflowLabel(status)
}

// Get status badge class
const getStatusBadgeClass = (status: Tender['status']): string => {
  return 'p-badge-' + getBadgeStatus(status)
}

// Check if tender is expired
const isExpired = computed(() => {
  if (!props.tender?.deadline) return false
  try {
    const deadline = new Date(props.tender.deadline)
    const now = new Date()
    return deadline < now
  } catch {
    return false
  }
})
</script>

<template>
  <div class="space-y-4">
    <!-- Dynamic warning message -->
    <div :class="['border-l-4 p-4', isExpired ? 'bg-red-50 border-red-400' : 'bg-yellow-50 border-yellow-400']">
      <div class="flex">
        <div class="flex-shrink-0">
          {{ isExpired ? '⚠️' : '📌' }}
        </div>
        <div class="ml-3">
          <p class="text-sm font-medium mb-1" :class="isExpired ? 'text-red-800' : 'text-yellow-800'">
            {{ isExpired ? 'Remove Expired Tender' : 'Remove from Watchlist' }}
          </p>
          <p class="text-sm" :class="isExpired ? 'text-red-700' : 'text-yellow-700'">
            {{ isExpired
              ? 'This tender has expired and will be removed from your watchlist.'
              : 'Are you sure you want to remove this tender from your watchlist?'
            }}
          </p>
        </div>
      </div>
    </div>

    <div v-if="props.tender" class="bg-gray-50 p-4 rounded-lg">
      <h3 class="text-sm font-medium text-gray-700 mb-2">Tender Details:</h3>
      <div class="space-y-3">
        <div>
          <p class="font-medium text-lg">{{ props.tender.referenceNo }}</p>
          <p class="text-sm text-gray-500">{{ props.tender.t247Id }}</p>
        </div>

        <div class="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span class="text-gray-600">Type:</span>
            <Badge
              :value="props.tender.sheetType"
              :class="'p-badge-' + getBadgeStatus(props.tender.sheetType?.toLowerCase())"
              class="ml-2 px-2 py-0.5 text-xs"
            />
          </div>
          <div>
            <span class="text-gray-600">Status:</span>
            <Badge
              :value="getStatusLabel(props.tender.status)"
              :class="getStatusBadgeClass(props.tender.status)"
              class="ml-2 px-2 py-0.5 text-xs"
            />
          </div>
          <div>
            <span class="text-gray-600">Deadline:</span>
            <span class="font-medium ml-2" :class="isExpired ? 'text-red-600' : ''">
              {{ formatDate(props.tender.deadline) }}
              <span v-if="isExpired" class="text-xs text-red-500 ml-1">(Expired)</span>
            </span>
          </div>
          <div>
            <span class="text-gray-600">Est. Cost:</span>
            <span class="font-medium ml-2">₹{{ props.tender.estimatedCost?.toLocaleString('en-IN') }}</span>
          </div>
        </div>

        <div class="pt-2 border-t border-gray-200">
          <p class="text-sm text-gray-700 line-clamp-2">
            {{ props.tender.tenderBrief || 'No description available' }}
          </p>
        </div>

        <div v-if="props.tender.organization" class="pt-2 border-t border-gray-200">
          <span class="text-gray-600">Organization:</span>
          <p class="text-sm font-medium mt-1">{{ props.tender.organization }}</p>
        </div>

        <div v-if="props.tender.location" class="pt-2 border-t border-gray-200">
          <span class="text-gray-600">Location:</span>
          <p class="text-sm font-medium mt-1">{{ props.tender.location }}</p>
        </div>

        <!-- Preview of action -->
        <div class="pt-2 border-t border-gray-200">
          <div class="flex items-center gap-2">
            <span class="text-gray-600">Action:</span>
            <Badge
              value="Will be removed from watchlist"
              class="bg-purple-100 text-purple-800 border border-purple-200 px-2 py-0.5 text-xs"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
